import { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Share2, Users, TrendingUp, Trophy } from "lucide-react";
import { api, brl, pct } from "@/lib/api";
import { TopBar, NotificationBell } from "@/components/Shell";
import RankingCard from "@/components/RankingCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Referral() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/referral/me").then(({ data }) => setR(data)).catch(() => {}); }, []);
  if (!r) return <div className="p-6 text-sm text-gray-500">Carregando indicações…</div>;

  const fullLink = `${window.location.origin}/login?ref=${r.code}`;
  const copy = async (text, msg) => {
    try { await navigator.clipboard.writeText(text); toast.success(msg); }
    catch { toast.error("Não foi possível copiar"); }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Time Food", text: "Peça comida e ganhe indicando!", url: fullLink }); }
      catch { /* cancelado */ }
    } else copy(fullLink, "Link copiado!");
  };

  return (
    <div data-testid="referral-page" className="pb-8">
      <TopBar subtitle="Indique e ganhe" right={<NotificationBell />} />

      <div className="px-4 pt-5">
        <div className="rounded-3xl bg-gf-black text-white p-5 relative overflow-hidden" data-testid="referral-hero">
          <span className="text-[10px] font-bold uppercase tracking-wider bg-gf-orange px-2 py-1 rounded-full">
            Renda recorrente
          </span>
          <h1 className="font-display text-2xl font-extrabold mt-3 leading-tight">
            Um salário mínimo ou mais<br />só indicando o app da sua cidade
          </h1>
          <p className="text-xs text-white/75 mt-2">
            Você ganha <b className="text-gf-orange">{pct(r.pct_l1)}</b> de tudo que seus indicados consumirem e
            <b className="text-gf-orange"> {pct(r.pct_l2)}</b> do segundo nível. Para sempre, em cada pedido.
          </p>
        </div>

        <div className="mt-4 rounded-2xl border border-gray-200 p-4 flex flex-col items-center" data-testid="referral-qr-card">
          <QRCodeCanvas value={fullLink} size={148} bgColor="#ffffff" fgColor="#141414" includeMargin />
          <p className="mt-3 text-[11px] text-gray-500">Seu código de indicação</p>
          <p className="font-display text-2xl font-extrabold tracking-wider" data-testid="referral-code">{r.code}</p>
          <div className="flex gap-2 mt-3 w-full">
            <Button data-testid="copy-link-button" variant="outline" onClick={() => copy(fullLink, "Link copiado!")}
              className="flex-1 h-11 rounded-xl font-bold">
              <Copy size={15} className="mr-1" /> Copiar link
            </Button>
            <Button data-testid="share-link-button" onClick={share}
              className="flex-1 h-11 rounded-xl gf-gradient text-white font-bold gf-press">
              <Share2 size={15} className="mr-1" /> Compartilhar
            </Button>
          </div>
          <p className="text-[10px] text-gray-400 mt-2 break-all text-center">{fullLink}</p>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-4">
          <Box testId="ref-level1-count" icon={Users} label="Nível 1" value={r.level1_count} />
          <Box testId="ref-level2-count" icon={Users} label="Nível 2" value={r.level2_count} />
          <Box testId="ref-earned-total" icon={TrendingUp} label="Ganhos" value={brl(r.earned_total)} accent />
        </div>

        <RankingCard />

        <div className="mt-5 rounded-2xl border border-gray-200 p-4">
          <h2 className="font-display font-bold text-base flex items-center gap-2">
            <Trophy size={16} className="text-mage-yellow" /> Como funciona a divisão de 12%
          </h2>
          <ul className="mt-2 text-xs text-gray-600 space-y-1">
            <li>• 3,5% Prefeitura da cidade</li>
            <li>• 2,5% administradora Time Food (+ R$ 0,99 de taxa de serviço)</li>
            <li>• {pct(r.pct_l1)} quem indicou o cliente (nível 1)</li>
            <li>• {pct(r.pct_l2)} quem indicou o indicador (nível 2)</li>
          </ul>
          <p className="text-[11px] text-mage-green font-semibold mt-2">
            As comissões caem na sua carteira na hora em que o pedido é feito.
          </p>
        </div>

        <h2 className="font-display font-bold text-base mt-6">Minha rede</h2>
        <div className="grid gap-2 mt-2" data-testid="referral-network">
          {[...r.level1.map((u) => ({ ...u, level: 1 })), ...r.level2.map((u) => ({ ...u, level: 2 }))].map((u, i) => (
            <div key={i} className="rounded-2xl border border-gray-200 p-3 flex justify-between items-center">
              <div>
                <p className="font-semibold text-sm">{u.name}</p>
                <p className="text-[11px] text-gray-500">{u.role === "consumer" ? "Cliente" : u.role === "merchant" ? "Lojista" : "Motoboy"}</p>
              </div>
              <span className="text-[10px] font-bold px-2 py-1 rounded-full text-white"
                style={{ background: u.level === 1 ? "#FF7A1A" : "#1F3864" }}>
                NÍVEL {u.level}
              </span>
            </div>
          ))}
          {r.level1_count + r.level2_count === 0 && (
            <p className="text-xs text-gray-500">Você ainda não tem indicados. Compartilhe seu link agora!</p>
          )}
        </div>

        <h2 className="font-display font-bold text-base mt-6">Comissões recebidas</h2>
        <div className="grid gap-2 mt-2" data-testid="referral-entries">
          {r.entries.map((e) => (
            <div key={e.id} className="rounded-2xl border border-gray-200 p-3 flex justify-between items-center">
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{e.description}</p>
                <p className="text-[11px] text-gray-500">{new Date(e.created_at).toLocaleDateString("pt-BR")}</p>
              </div>
              <p className="font-display font-extrabold text-mage-green ml-2">+ {brl(e.amount)}</p>
            </div>
          ))}
          {r.entries.length === 0 && <p className="text-xs text-gray-500">Nenhuma comissão ainda.</p>}
        </div>
      </div>
    </div>
  );
}

const Box = ({ icon: Icon, label, value, accent, testId }) => (
  <div data-testid={testId} className="rounded-2xl border p-3 bg-white" style={{ borderColor: accent ? "#FF7A1A" : "#E5E7EB" }}>
    <Icon size={15} className={accent ? "text-gf-orange" : "text-gray-400"} />
    <p className="text-[10px] text-gray-500 mt-1">{label}</p>
    <p className="font-display text-base font-extrabold">{value}</p>
  </div>
);
